import { createSelector } from 'reselect';

const getApptSchedulesData = (state) => state.screens.scheduler.scheduleData;
const getApptsOverridesData = (state) => state.screens.scheduler.aptsSlotOverrideData;

import { getCurrentCase, getCurrentCasePatient } from '../cases/selector';

import {
  getGroupedPatientsForEmployer,
  getGroupedPatientsByTaskingIdForEmployer,
  getCurrentEmployerTimezone,
} from '../../selectors/employers';

import { getEventsArrayForCurrentEmployer } from '../../selectors/entities';

import { currentImmunizationsForPatient, currentDeploymentInfoForPatient } from './helpers';

import moment from 'moment';

const getVaccinesByRegion = (state) => state.entities.metaData.vaccinesByRegion;

import { convertDateFromTZ } from '../calendar/timezone_helper';

export const ALL_DAY_TIME = '00:00';
import {
  APPT_TYPES,
  EVENT_TYPE_AIRFORCE_DEPLOYMENT as EVENT_TYPE_READINESS,
} from '../../constants/eventTypes';

function _requiredAndCompleteImmunizationsForPatient(c, patient, vaccinesByRegion) {
  const current = Object.keys(currentImmunizationsForPatient(patient));

  const deploymentData = currentDeploymentInfoForPatient(patient);
  const deploymentLocation = deploymentData.location;

  let required = [];
  if (deploymentLocation) {
    const vaccinesForLocation = vaccinesByRegion[deploymentLocation];
    if (vaccinesForLocation) {
      required = vaccinesForLocation.map((vaccine) => vaccine.name);
    }
  }

  const complete = required.filter((item) => current.includes(item));

  return { required, complete, group: c.group };
}

export const getRequiredAndCompleteImmunesForCurrentCasePatient = createSelector(
  getCurrentCase,
  getCurrentCasePatient,
  getVaccinesByRegion,
  _requiredAndCompleteImmunizationsForPatient
);

// Uncomment all this stuff once we're ready to generate the reports from the patient data
// thats in our system. Currently we're importing the data from external sources and configuring
// it in the uiConfig property of the employer via server side scripts.
// export const getPatientsForEmployer = createSelector(
//   getCasesForEmployer,
//   cases => {
//     return cases.map(c => {
//       return c.patient;
//     });
//   }
// );

// export const getRequiredAndCompleteImmunizationsForPatients = createSelector(
//   getPatientsForEmployer,
//   getVaccinesByRegion,
//   (patients, vaccinesByRegion) => {
//     return patients.map(p => {
//       return _requiredAndCompleteImmunizationsForPatient(p, vaccinesByRegion);
//     });
//   }
// );
// export const getImmunizationsCompleteByVaccineForEmployer = createSelector(
//   getRequiredAndCompleteImmunizationsForPatients,
//   results => {
//     const resultsByVaccine = {};
//
//     results.forEach(r => {
//       r.required.forEach(req => {
//         if (!resultsByVaccine[req])
//           resultsByVaccine[req] = { required: 0, complete: 0 };
//
//         resultsByVaccine[req].required += 1;
//         if (_.includes(r.complete, req)) resultsByVaccine[req].complete += 1;
//       });
//     });
//
//     return resultsByVaccine;
//   }
// );
// export const getOverallImmunizationsCompleteForEmployer = createSelector(
//   getRequiredAndCompleteImmunizationsForPatients,
//   results => {
//     let totalRequired = 0.0;
//     let totalComplete = 0.0;
//
//     results.forEach(r => {
//       totalRequired += r.required.length;
//       totalComplete += r.complete.length;
//     });
//
//     return totalComplete / totalRequired;
//   }
// );
// export const getGroupImmunizationsCompleteForEmployer = createSelector(
//   getRequiredAndCompleteImmunizationsForPatients,
//   results => {
//     let totalRequired = 0.0;
//     let totalComplete = 0.0;
//
//     results.forEach(r => {
//       totalRequired += r.required.length;
//       totalComplete += r.complete.length;
//     });
//
//     return totalComplete / totalRequired;
//   }
// );

function _requiredAndCompleteDeploymentForPatient(patient) {
  const deploymentData = currentDeploymentInfoForPatient(patient);
  const taskingId = deploymentData?.taskingId;
  const ready = deploymentData?.ready?.clearanceGranted;

  return { required: !!taskingId, complete: !!ready };
}
function _requiredAndCompleteDeploymentByGroup(groups) {
  const resultsByGroup = {};

  Object.keys(groups).forEach((groupName) => {
    if (!resultsByGroup[groupName])
      resultsByGroup[groupName] = { required: 0, complete: 0, percent: 0 };
    const groupResults = resultsByGroup[groupName];
    const groupCases = groups[groupName];
    groupCases.forEach((c) => {
      const patientResult = _requiredAndCompleteDeploymentForPatient(c.patient);
      if (patientResult.required) groupResults.required++;
      if (patientResult.complete) groupResults.complete++;
    });
    groupResults.percent = groupResults.complete / groupResults.required;
  });

  return resultsByGroup;
}

export const getReadinessCompleteByGroupForEmployer = createSelector(
  getGroupedPatientsForEmployer,
  _requiredAndCompleteDeploymentByGroup
);
export const getReadinessCompleteByTaskingIdForEmployer = createSelector(
  getGroupedPatientsByTaskingIdForEmployer,
  _requiredAndCompleteDeploymentByGroup
);

const getApptEventsForEmployer = createSelector(getEventsArrayForCurrentEmployer, (events) => {
  if (!events) return [];

  return events.filter((ce) => {
    return APPT_TYPES.includes(ce.eventTypeName);
  });
});

function _shouldHideOverride(e, eventsForStartDate) {
  if (!e.overrideId) return false; // if this is not an override, then we don't skip it
  if (e.allDay && !e.employerSchedulerHoursId) return false; // if this is an all day override for all schedules, then we don't skip it
  if (eventsForStartDate.find((ev) => ev.allDay && !ev.employerSchedulerHoursId)) return true; // if we have an all day override for all schedules, then we skip this one
  if (
    !e.allDay &&
    eventsForStartDate.find(
      (ev) => ev.allDay && ev.employerSchedulerHoursId === e.employerSchedulerHoursId
    )
  ) {
    return true; // if we have an all day override for this schedule, then we skip this one
  }

  return false; // otherwise we don't skip it
}

function _reduceApptData(eventType, events) {
  // the array will get only one per date for display purposes to do with
  // the calendar UI component that we're using
  const eventsArrayForCal = [];

  // the hash will have all events hashed by start date date only
  const eventsByStartDate = events.reduce((result, current) => {
    if (eventType && current.eventTypeName !== eventType) return result;

    const startDateDateOnly = moment(current.startDate).format('YYYY-MM-DD');
    if (!result[startDateDateOnly]) {
      result[startDateDateOnly] = [];
      if (!current.allDay || current.eventTypeName === EVENT_TYPE_READINESS)
        eventsArrayForCal.push(current);
    }
    result[startDateDateOnly].push(current);
    return result;
  }, {});

  const eventsByTime = Object.keys(eventsByStartDate).reduce((result, current) => {
    result[current] = eventsByStartDate[current].reduce((innerResult, e) => {
      if (_shouldHideOverride(e, eventsByStartDate[current])) return innerResult;

      const timeStr = e.allDay ? 'All Day' : `${moment(e.startDate).format('HH:mm')}`;
      innerResult[timeStr] = (innerResult[timeStr] || []).concat(e);
      return innerResult;
    }, {});
    return result;
  }, {});

  return { eventsArrayForCal, eventsByStartDate, eventsByTime };
}

export const getApptOverrides = createSelector(
  getApptsOverridesData,
  getApptSchedulesData,
  getCurrentEmployerTimezone,
  (overrides, schedules, employerTimezone) => {
    const overrideEvents = (overrides || [])
      .map((override) => {
        const schedule = schedules.find(
          (s) => s.employerSchedulerHoursId === override.employerSchedulerHoursId
        );
        return {
          ...override,
          startDate: convertDateFromTZ(override.startDate, employerTimezone),
          scheduleName: schedule?.name,
        };
      })
      .filter(Boolean)
      .sort(function (a, b) {
        if (a.startDate < b.startDate) return -1;
        return 1;
      });
    return _reduceApptData(null, overrideEvents);
  }
);

export const getApptEventsForCalendarDisplay = createSelector(
  getApptEventsForEmployer,
  (apptEvents) => {
    // convert them to local time
    apptEvents = apptEvents.map((event) => {
      return {
        ...event,
        startDate: convertDateFromTZ(event.startDate, event.timeZone).toString(),
        endDate: convertDateFromTZ(event.endDate, event.timeZone).toString(),
        origStartDate: event.startDate,
      };
    });

    return APPT_TYPES.reduce((result, current) => {
      result[current] = _reduceApptData(current, apptEvents);
      return result;
    }, {});
  }
);
